import BookmarkAddRoundedIcon from "@mui/icons-material/BookmarkAddRounded";
import BookmarkRemoveRoundedIcon from "@mui/icons-material/BookmarkRemoveRounded";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import UndoRoundedIcon from "@mui/icons-material/UndoRounded";
import { Button } from "@mui/material";

import { AppointmentForTakeoverResponse } from "@/api";
import { TakeoverView } from "@/pages/takeover/Takeover.types";
import { isTakenOver } from "@/pages/takeover/Takeover.utils";
import { useTranslation } from "@/translations";

interface Props {
  row: AppointmentForTakeoverResponse;
  view: TakeoverView;
  // undefined for a user without a counselor record of their own, who holds no appointment.
  currentCounselorId: string | undefined;
  isBusy: boolean;
  onTakeOver: (row: AppointmentForTakeoverResponse) => void;
  onReverse: (row: AppointmentForTakeoverResponse) => void;
  onMark: (row: AppointmentForTakeoverResponse) => void;
  onUnmark: (row: AppointmentForTakeoverResponse) => void;
}

/**
 * Renders the per-row action for the takeover board.
 * - "toTakeOver" (Zu übernehmen) tab: the list is not filtered by holder, so an appointment the
 *   current user holds is listed with no action at all; every other one offers "Termin übernehmen",
 *   including re-taking over an appointment that someone else already took over.
 * - "toRelease" (Termine freigeben) tab: mark a free appointment, unmark one that is still open, or
 *   reset a taken-over one (reverse) so it becomes available to be taken over again.
 */
export function TakeoverActionCell({
  row,
  view,
  currentCounselorId,
  isBusy,
  onTakeOver,
  onReverse,
  onMark,
  onUnmark,
}: Props) {
  const { t } = useTranslation();
  const takenOver = isTakenOver(row);

  // The appointment is "the user's own" when they are the current holder (assigned) or the original
  // owner shown in the VON column (previous).
  const isMine =
    currentCounselorId != null &&
    (row.assignedCounselorId === currentCounselorId || row.previousCounselorId === currentCounselorId);

  if (view === "toTakeOver") {
    // A user cannot take over an appointment that is already theirs (own or taken over from them).
    if (isMine) {
      return null;
    }

    return (
      <Button
        variant="text"
        size="small"
        disabled={isBusy}
        startIcon={<PersonAddAlt1RoundedIcon />}
        aria-label={t("takeover.actions.takeOver")}
        onClick={() => onTakeOver(row)}
      >
        {t("takeover.actions.takeOver")}
      </Button>
    );
  }

  // view === "toRelease"
  if (takenOver) {
    // Reset a taken-over appointment so it returns to "zu übernehmen" and can be taken over again.
    return (
      <Button
        variant="text"
        size="small"
        color="error"
        disabled={isBusy}
        startIcon={<UndoRoundedIcon />}
        aria-label={t("takeover.actions.reverse")}
        onClick={() => onReverse(row)}
      >
        {t("takeover.actions.reverse")}
      </Button>
    );
  }

  if (row.isMarkedForTakeover) {
    return (
      <Button
        variant="text"
        size="small"
        color="error"
        disabled={isBusy}
        startIcon={<BookmarkRemoveRoundedIcon />}
        aria-label={t("takeover.actions.unmark")}
        onClick={() => onUnmark(row)}
      >
        {t("takeover.actions.unmark")}
      </Button>
    );
  }

  return (
    <Button
      variant="text"
      size="small"
      disabled={isBusy}
      startIcon={<BookmarkAddRoundedIcon />}
      aria-label={t("takeover.actions.mark")}
      onClick={() => onMark(row)}
    >
      {t("takeover.actions.mark")}
    </Button>
  );
}
